import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useDownloadTerminalServiceDocument } from "@features/terminal-services/hooks/useDownloadTerminalServiceDocument";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import {
  FottServiceRequestStatus,
  type TerminalServiceTaskDecision,
} from "@services/terminal-services/types";
import { useTranslation } from "react-i18next";

type StatusDetailsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  taskDecision?: TerminalServiceTaskDecision | null;
};

const SERVICE = "bff-terminal-service";
const TEST_ID_BASE = "terminal-service-status-details";
const LANG_BASE = "terminal-services.details.status-details";

const StatusDetailsDrawer = ({ isOpen, onClose, taskDecision }: StatusDetailsDrawerProps) => {
  const { t } = useTranslation();
  const { handleDownload, handlePreview } = useDownloadTerminalServiceDocument();
  const isFottUser = useAuthStore((state) => state.user?.roles.includes("fott-user") ?? false);
  const isApproved = taskDecision?.status === FottServiceRequestStatus.APPROVED;
  const isCancelled = taskDecision?.status === FottServiceRequestStatus.CANCELLED;
  const rejectionDocument = taskDecision?.rejectionDocument;
  const decisionDocument = isApproved
    ? taskDecision?.bookingConfirmation
    : isCancelled
      ? taskDecision?.cancellationDocument
      : rejectionDocument;

  if (!isOpen) {
    return null;
  }

  return (
    <div data-testid={`${TEST_ID_BASE}-drawer`} className="min-w-[400px]">
      <SideDrawer
        isOpen={isOpen}
        title={t(`${LANG_BASE}.title`)}
        onClose={onClose}
        submitLabel={t("common.close")}
        onSubmit={onClose}
        hideCancelButton
      >
        <div className="w-[400px] p-6 space-y-4">
          {isApproved && isFottUser && taskDecision?.jobType && (
            <div data-testid={`${TEST_ID_BASE}-job-type`}>
              <div className="mb-1 text-sm font-bold">{t(`${LANG_BASE}.job-type`)}</div>
              <p className="text-sm">{taskDecision.jobType}</p>
            </div>
          )}
          {isApproved && taskDecision?.operatorName && (
            <div data-testid={`${TEST_ID_BASE}-operator-name`}>
              <div className="mb-1 text-sm font-bold">{t(`${LANG_BASE}.operator-name`)}</div>
              <p className="text-sm whitespace-pre-wrap break-words">{taskDecision.operatorName}</p>
            </div>
          )}
          {(isApproved
            ? taskDecision?.approvalNotes
            : isCancelled
              ? taskDecision?.cancellationReason
              : taskDecision?.rejectionNotes) && (
            <div data-testid={`${TEST_ID_BASE}-comments`}>
              <div className="mb-1 text-sm font-bold">
                {t(isCancelled ? `${LANG_BASE}.cancellation-reason` : `${LANG_BASE}.comments`)}
              </div>
              <p className="text-sm whitespace-pre-wrap break-words">
                {isApproved
                  ? taskDecision?.approvalNotes
                  : isCancelled
                    ? taskDecision?.cancellationReason
                    : taskDecision?.rejectionNotes}
              </p>
            </div>
          )}

          {decisionDocument?.documentUrl && (
            <div data-testid={`${TEST_ID_BASE}-attachments`}>
              <div className="mb-1 text-sm font-bold">
                {t(isApproved ? `${LANG_BASE}.booking-confirmation` : `${LANG_BASE}.attachments`)}
              </div>
              <div className="border rounded p-3 w-full border-[#F1F2F4]">
                <div className="flex justify-between items-center w-full">
                  <a
                    href={decisionDocument.documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-all cursor-pointer"
                    data-testid={`${TEST_ID_BASE}-attachment-link`}
                    onClick={(event) => {
                      event.preventDefault();
                      void handlePreview(SERVICE, decisionDocument.documentUrl);
                    }}
                  >
                    {decisionDocument.documentName ?? decisionDocument.documentUrl}
                  </a>
                  <button
                    type="button"
                    className="cursor-pointer"
                    title={t("common.download")}
                    data-testid={`${TEST_ID_BASE}-attachment-download`}
                    onClick={() =>
                      void handleDownload(
                        SERVICE,
                        decisionDocument.documentUrl,
                        decisionDocument.documentName ?? decisionDocument.documentUrl,
                      )
                    }
                  >
                    <FileDownloadOutlinedIcon />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </SideDrawer>
    </div>
  );
};

export default StatusDetailsDrawer;
